/**
 * rd-r12 A2-6 — the ONE booking-location rule (owner decision 6).
 *
 * A hub answer must never be booked at an alphabetical first location: the
 * list comes from the company's chart snapshot, where the first spelling can
 * be a retired alias and `chart_locations` cannot yet tell a current location
 * from an alias (the deferred alias flag, brief note N6). A person picks
 * instead.
 *
 * Decided order:
 * 1. exactly one location → `sole` (even when that one is `na`);
 * 2. otherwise a listed `na` → `na` (the ledger convention);
 * 3. otherwise no locations at all → `na` (a company whose chart snapshot has
 *    not arrived yet keeps booking, orchestrator design call H1);
 * 4. otherwise → `choose`: the card stays pending for the account manager.
 *
 * Used by the hub answer, the hub-minted rule and the portal's sole-location
 * prefill, so all three agree by construction. Leaf module: pure, no Env, no
 * D1. Synthetic data only.
 */

export type BookingLocationDecision =
  | { kind: "na" }
  | { kind: "sole"; location: string }
  | { kind: "choose" };

/**
 * The internal `ai_rationale` the location-needed path stores on the pending
 * card (R12 A2-6). It is a MARKER, never shown to a person: the Worker writes
 * it and the portal selects the hub's own sentence with it, so both sides read
 * one value instead of repeating the text and drifting apart.
 */
export const HUB_LOCATION_NEEDED_RATIONALE = "hub answer: location needed";

/** Where a hub answer may book, or that a person has to choose. */
export function defaultBookingLocation(
  locations: readonly string[],
): BookingLocationDecision {
  if (locations.length === 1) {
    return { kind: "sole", location: locations[0] ?? "" };
  }
  if (locations.length === 0) {
    return { kind: "na" };
  }
  if (locations.includes("na")) {
    return { kind: "na" };
  }
  return { kind: "choose" };
}

/** The location a decision books at, or `null` when a person must choose. */
export function bookingLocationOrNull(
  decision: BookingLocationDecision,
): string | null {
  if (decision.kind === "choose") return null;
  return decision.kind === "sole" ? decision.location : "na";
}
